// Schemas

export interface DatesFragment {
  created_at: string;
  updated_at: string;
}

export enum UserAccountType {
  Account = 'Account',
}

export enum UserRole {
  Admin = 'admin',
  Editor = 'editor',
  Reader = 'reader',
  Custom = 'custom',
  SuperAdmin = 'super-admin',
}

export enum FeatureKeys {
  FraudPrevention = 'fraud_prevention',
  PurchaseVerification = 'purchase_verification',
  AdRevenue = 'ad_revenue',
  WebAttribution = 'web_attribution',
  AdjustAutomate = 'adjust_automate',
  UninstallReinstall = 'uninstall_reinstall',
  AudienceBuilderFacebookLinking = 'audience_builder_facebook_linking',
  AcquiredIo = 'acquired_io',
  ImpressionTracking = 'impression_tracking',
  ClickInjectionFiltering = 'click_injection_filtering',
  Segmentation = 'segmentation',
  TencentKpiAccess = 'tencent_kpi_access',
  TrackerAttributionSettings = 'tracker_attribution_settings',
  RevenueData = 'revenue_data',
  GoogleOrganicSearch = 'google_organic_search',
  AttributionSettings = 'attribution_settings',
  Retry4xxCallbacks = 'retry_4xx_callbacks',
  Tencent = 'tencent',
  FirstSourceDynamicSource = 'first_source_dynamic_source',
  ApiAccess = 'api_access',
  TvTracking = 'tv_tracking',
  RawExportSettings = 'raw_export_settings',
  SegmentationGroupLinking = 'segmentation_group_linking',
  DedicatedAccountManager = 'dedicated_account_manager',
  Snapchat = 'snapchat',
  CostData = 'cost_data',
  Multitouch = 'multitouch',
  AcquiredAdj1SecretsAccess = 'acquired_adj1_secrets_access',
  Imei = 'imei',
  SdkSignature = 'sdk_signature',
  Attribution = 'attribution',
  ReportingTimezone = 'reporting_timezone',
  FacebookDataIntegration = 'facebook_data_integration',
  AdvancedPermissions = 'advanced_permissions',
  SKAdNetwork = 'skadnetwork',
  FingerprintingIos14 = 'fingerprinting_ios_14',
  IosAppVerification = 'ios_app_verification',
  AllowNewDashboardByDefault = 'allow_new_dashboard_by_default',
  DisplayClassicDashboard = 'display_classic_dashboards',
}

export interface FeatureProps {
  enabled: boolean;
  available?: boolean;
}

export type Features = { [field in FeatureKeys]?: FeatureProps };
export type UserTimezone = Timezone & DatesFragment;

export interface User {
  id: number;
  email: string;
  name: string;
  main_account_id: number;
  main_account_type: UserAccountType;
  created_by: Nullable<string>;
  created_at: string;
  updated_at: string;
  authentication_token: string;
  locale: string;
  uses_next: boolean;
  api_access: boolean;
  first_name: string;
  last_name: string;
  super_admin: boolean;
  salesforce_sync_failed: boolean;
  timezone_id: Nullable<number>;
  uses_dash: boolean;
  sso: boolean;
  confirmed: boolean;
  account_ids: number[];
  full_name: string;
  role: UserRole;
  has_local_roles: boolean;
  current_account_id: number;
  language: string;
  unconfirmed_email: Nullable<string>;
  timezone: Nullable<UserTimezone>;
  multiple_accounts_view: boolean;
  features: Features;
  in_demo_period?: boolean;
  advanced?: boolean;
  country?: Nullable<string>;
  region?: Nullable<string>;
}

export interface AdvancedUser {
  id: number;
  full_name: string;
  email: string;
}

export interface Timezone {
  id: number;
  name: string;
  abbrev: string;
  utc_offset: string;
  label: string;
}

export enum AccountState {
  Active = 'active',
  WaitingForData = 'waiting_for_data',
  SetupApp = 'setup_app',
  Paused = 'paused',
}

export enum AccountPlanName {
  Basic = 'Basic',
  Business = 'Business',
  BusinessPro = 'Business Pro',
  Enterprise = 'Enterprise',
  Flat = 'Flat',
}

export interface AccountPaymentPlan {
  id: number;
  name: string;
  base_price: number;
  attribution_price: number;
  account_id: number;
  currency: string;
  active_since: string;
  included_attributions: number;
  created_at: string;
  updated_at: string;
  payment_plan_template_id: number;
  unlimited: boolean;
  current: boolean;
  scheduled: boolean;
  next_billing_date: string;
  features: Features;
  yearly: boolean;
}

export interface AccountPackage {
  id: number;
  account_id: number;
  name: string;
  active_since: string;
}

export interface AccountAddress {
  id: number;
  line_1: string;
  line_2: string;
  city: string;
  state: string;
  country_code: string;
  zip_code: string;
  addressable_id: number;
  addressable_type: string;
  company: string;
  contact_person: string;
  created_at: string;
  updated_at: string;
  european: boolean;
  country_name: string;
  complete: boolean;
}

interface AccountContract {
  id: number;
  included_attributions: number;
  tier: string;
  price: number;
  features: Array<string>;
  contract_length: number;
  currency: string;
}

interface SalesForceOrder {
  metric_type: string;
  included_metric_amount: number;
  billing_cycle: string;
  total_order_amount: number;
  metric_unit_price: number;
  currency: string;
  plan_type: string;
}

export type AccountTimezone = Timezone & DatesFragment;

export interface Account {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
  main_contact_id: number;
  has_api_access: boolean;
  features: Features;
  currency_iso_code: string;
  users_count: number;
  active: boolean;
  state: AccountState | string;
  in_demo_period: boolean;
  running: boolean;
  missing_payment_information: boolean;
  disabled: boolean;
  payment_plan: AccountPaymentPlan;
  has_payment_method: boolean;
  packages: AccountPackage[];
  address: AccountAddress;
  timezone_id: number;
  timezone: AccountTimezone;
  allow_monthly_pricing: boolean;
  manager: string;
  billing_day: number;
  contract: Nullable<AccountContract>;
  contract_ends_on: Nullable<string>;
  contract_length: Nullable<number>;
  sales_manager?: string;
  payment_method?: string;
  new_feature_scope: boolean;
  new_billing: boolean;
  salesforce_order: Nullable<SalesForceOrder>;
}

export type PendoTrackEventMetadata = {
  [key: string]: string | number | boolean;
};
